import { useState } from "react";
import { auth } from "../../application/firebase";
import { BounceLoader } from "react-spinners";

const FUNCTIONS_BASE =
  "https://us-central1-inmobiliariaplorutti-7aedb.cloudfunctions.net";

/**
 * Build the proxy URL for an Instagram image.
 */
function getProxyImageUrl(originalUrl) {
  return `${FUNCTIONS_BASE}/proxyInstagramImage?url=${encodeURIComponent(originalUrl)}`;
}

/**
 * Download an image from URL and convert it to a File object.
 */
async function urlToFile(url, filename) {
  const proxyUrl = getProxyImageUrl(url);
  const response = await fetch(proxyUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

/**
 * Geocode an address via our Cloud Function (calls Nominatim server-side).
 * Returns { lat, lng } or null.
 */
async function geocodeAddress(address, locality) {
  const query = [address, locality, "Argentina"].filter(Boolean).join(", ");
  try {
    const response = await fetch(
      `${FUNCTIONS_BASE}/geocodeAddress?q=${encodeURIComponent(query)}`
    );
    if (!response.ok) return null;
    const data = await response.json();
    return { lat: data.lat, lng: data.lng };
  } catch {
    return null;
  }
}

/**
 * Build a Google Maps embed URL from lat/lng.
 */
function buildEmbedUrl(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}&z=16&output=embed`;
}

function MapConfirmModal({ lat: initialLat, lng: initialLng, address, locality, onConfirm, onCancel }) {
  const [currentLat, setCurrentLat] = useState(initialLat);
  const [currentLng, setCurrentLng] = useState(initialLng);
  const [searchQuery, setSearchQuery] = useState(
    [address, locality].filter(Boolean).join(", ")
  );
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const embedUrl = buildEmbedUrl(currentLat, currentLng);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchError(null);
    const coords = await geocodeAddress(searchQuery, "");
    if (coords) {
      setCurrentLat(coords.lat);
      setCurrentLng(coords.lng);
    } else {
      setSearchError("No se encontró esa dirección. Probá con más detalle.");
    }
    setSearching(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-2xl mx-4">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-700">
            Confirmar ubicación
          </h3>
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Escribí la dirección correcta..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {searching ? "Buscando..." : "Buscar"}
            </button>
          </div>
          {searchError && (
            <p className="text-red-500 text-xs mt-1">{searchError}</p>
          )}
        </div>
        <div className="h-80">
          <iframe
            key={`${currentLat}-${currentLng}`}
            width="100%"
            height="100%"
            frameBorder="0"
            title="map-preview"
            scrolling="no"
            src={embedUrl}
          ></iframe>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Omitir mapa
          </button>
          <button
            type="button"
            onClick={() => onConfirm(buildEmbedUrl(currentLat, currentLng))}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Ubicación correcta
          </button>
        </div>
      </div>
    </div>
  );
}

function InstagramImport({ onImportComplete }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [localidad, setLocalidad] = useState("");
  const [mapModal, setMapModal] = useState(null); // { lat, lng, address, locality, fields, files }

  const handleScrape = async () => {
    if (!url.trim()) return;

    setLoading(true);
    setError(null);
    setPreview(null);
    setLocalidad("");

    try {
      const user = auth.currentUser;
      if (!user) {
        setError("Debes iniciar sesión para importar desde Instagram.");
        return;
      }
      const token = await user.getIdToken();

      const response = await fetch(`${FUNCTIONS_BASE}/scrapeInstagramPost`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ url: url.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Error del servidor (${response.status})`);
      }

      const data = await response.json();
      setPreview(data);

      if (data.parsedFields?.country) {
        setLocalidad(data.parsedFields.country);
      }
    } catch (err) {
      console.error("Error al importar desde Instagram:", err);
      setError(
        err.message || "Error al obtener datos del post. Verificá que la URL sea correcta y el post sea público."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!preview) return;

    setImporting(true);
    setError(null);

    try {
      const filePromises = preview.images.map((imgUrl, index) =>
        urlToFile(imgUrl, `instagram_${index + 1}.jpg`)
      );
      const files = await Promise.all(filePromises);

      const fields = { ...preview.parsedFields };
      if (localidad.trim()) {
        fields.country = localidad.trim();
      }

      const address = fields.address || "";
      const loc = localidad.trim();

      // If we have address or locality, try geocoding and show map modal
      if (address || loc) {
        const coords = await geocodeAddress(address, loc);
        if (coords) {
          setMapModal({
            lat: coords.lat,
            lng: coords.lng,
            address,
            locality: loc,
            fields,
            files,
          });
          setImporting(false);
          return;
        }
      }

      // No address/locality or geocoding failed → import without maps
      onImportComplete({ fields, files });
      setPreview(null);
      setUrl("");
      setLocalidad("");
    } catch (err) {
      console.error("Error al descargar imágenes:", err);
      setError("Error al descargar las imágenes. Intentá de nuevo.");
    } finally {
      setImporting(false);
    }
  };

  const handleMapConfirm = (embedUrl) => {
    const { fields, files } = mapModal;
    fields.maps = embedUrl;
    onImportComplete({ fields, files });
    setMapModal(null);
    setPreview(null);
    setUrl("");
    setLocalidad("");
  };

  const handleMapCancel = () => {
    // Import without maps URL, user can set it manually
    const { fields, files } = mapModal;
    onImportComplete({ fields, files });
    setMapModal(null);
    setPreview(null);
    setUrl("");
    setLocalidad("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleScrape();
    }
  };

  const needsLocalidad = preview && !preview.parsedFields?.country;

  return (
    <div className="mb-6">
      {mapModal && (
        <MapConfirmModal
          lat={mapModal.lat}
          lng={mapModal.lng}
          address={mapModal.address}
          locality={mapModal.locality}
          onConfirm={handleMapConfirm}
          onCancel={handleMapCancel}
        />
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="https://www.instagram.com/p/..."
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          disabled={loading}
        />
        <button
          type="button"
          onClick={handleScrape}
          disabled={loading || !url.trim()}
          className="px-6 py-2 text-white font-medium rounded-md transition-opacity disabled:opacity-50"
          style={{
            background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
          }}
        >
          {loading ? "Importando..." : "Importar de Instagram"}
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <BounceLoader color="#bc1888" size={40} />
          <span className="ml-3 text-gray-500">Obteniendo datos del post...</span>
        </div>
      )}

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {preview && (
        <div className="mt-4 p-4 border border-purple-200 rounded-md bg-purple-50">
          <h3 className="font-semibold text-gray-700 mb-3">Preview del post</h3>

          {preview.images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
              {preview.images.map((imgUrl, index) => (
                <img
                  key={index}
                  src={getProxyImageUrl(imgUrl)}
                  alt={`Imagen ${index + 1}`}
                  className="w-full h-24 object-cover rounded border"
                />
              ))}
            </div>
          )}

          {preview.parsedFields && Object.keys(preview.parsedFields).length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-600 mb-2">Datos detectados:</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                {preview.parsedFields.type && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Tipo: {preview.parsedFields.type}
                  </span>
                )}
                {preview.parsedFields.state && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Estado: {preview.parsedFields.state}
                  </span>
                )}
                {preview.parsedFields.address && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Dirección: {preview.parsedFields.address}
                  </span>
                )}
                {preview.parsedFields.currency && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Moneda: {preview.parsedFields.currency}
                  </span>
                )}
                {preview.parsedFields.price && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Precio: {preview.parsedFields.price}
                  </span>
                )}
                {preview.parsedFields.ambientes && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Ambientes: {preview.parsedFields.ambientes}
                  </span>
                )}
                {preview.parsedFields.dormitorios && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Dormitorios: {preview.parsedFields.dormitorios}
                  </span>
                )}
                {preview.parsedFields.baños && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Baños: {preview.parsedFields.baños}
                  </span>
                )}
                {preview.parsedFields.patio && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Patio: {preview.parsedFields.patio}
                  </span>
                )}
                {preview.parsedFields.expensas && (
                  <span className="bg-white px-2 py-1 rounded border">
                    Expensas: {preview.parsedFields.expensas}
                  </span>
                )}
                {preview.parsedFields.contrato && (
                  <span className="bg-white px-2 py-1 rounded border col-span-2">
                    Contrato: {preview.parsedFields.contrato}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Locality input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-1">
              {needsLocalidad
                ? "Localidad (no detectada, completá para ubicar en el mapa)"
                : "Localidad"}
            </label>
            <input
              type="text"
              value={localidad}
              onChange={(e) => setLocalidad(e.target.value)}
              placeholder="Ej: Chascomús"
              className={`w-full sm:w-1/2 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                needsLocalidad
                  ? "border-yellow-400 bg-yellow-50"
                  : "border-gray-300 bg-white"
              }`}
            />
          </div>

          {preview.caption && (
            <details className="mb-4">
              <summary className="text-sm text-gray-500 cursor-pointer">
                Ver caption completo
              </summary>
              <p className="mt-2 text-sm text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                {preview.caption}
              </p>
            </details>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleConfirmImport}
              disabled={importing}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {importing ? "Procesando..." : "Usar estos datos"}
            </button>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default InstagramImport;
